import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, ReferenceLine } from "recharts";
import { useTrips } from "@/hooks/useTrips";
import { useExpenses, INITIAL_EXPENSE_TYPES } from "@/hooks/useExpenses";
import { useVehicles } from "@/hooks/useVehicles";
import { format, parseISO } from "date-fns";
import { getTripInstallments } from "@/lib/tripPayoutSchedule";
import { Skeleton } from "@/components/ui/skeleton";
import { Info } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

export const CashFlowChart = () => {
  const { trips, isLoading: tripsLoading } = useTrips();
  const { expenses, isLoading: expensesLoading } = useExpenses();
  const { vehicles } = useVehicles();

  const [includeInitial, setIncludeInitial] = useState(false);
  const [vehicleFilter, setVehicleFilter] = useState("all");

  const isLoading = tripsLoading || expensesLoading;

  const chartData = useMemo(() => {
    if ((!trips || trips.length === 0) && (!expenses || expenses.length === 0)) return [];

    const byMonth: Record<string, { earnings: number; expenses: number }> = {};

    const matchesVehicleFilter = (vehicleId: string | null | undefined) => {
      if (vehicleFilter === "all") return true;
      if (vehicleFilter === "operations") return !vehicleId;
      if (vehicleFilter === "all-vehicles") return !!vehicleId;
      return vehicleId === vehicleFilter;
    };

    trips?.forEach((trip) => {
      if (!matchesVehicleFilter(trip.vehicle_id)) return;
      const installments = getTripInstallments(trip);
      installments.forEach(({ date, amount }) => {
        const monthKey = format(date, "yyyy-MM");
        if (!byMonth[monthKey]) byMonth[monthKey] = { earnings: 0, expenses: 0 };
        byMonth[monthKey].earnings += amount;
      });
    });

    expenses?.forEach((expense) => {
      if (expense.expense_date && expense.amount != null) {
        if (!includeInitial && INITIAL_EXPENSE_TYPES.includes(expense.expense_type || "")) return;
        if (!matchesVehicleFilter(expense.vehicle_id)) return;
        try {
          const monthKey = format(parseISO(expense.expense_date), "yyyy-MM");
          if (!byMonth[monthKey]) byMonth[monthKey] = { earnings: 0, expenses: 0 };
          byMonth[monthKey].expenses += expense.amount;
        } catch { /* skip */ }
      }
    });

    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({
        month: format(parseISO(month + "-01"), "MMM yyyy"),
        cashFlow: Math.round((data.earnings - data.expenses) * 100) / 100,
        earnings: Math.round(data.earnings * 100) / 100,
        expenses: Math.round(data.expenses * 100) / 100,
      }));
  }, [trips, expenses, includeInitial, vehicleFilter]);

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Cash Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
            Cash Flow
            <TooltipProvider delayDuration={0}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs font-normal">
                  <p>
                    {includeInitial
                      ? "All expenses are included in this chart."
                      : 'Initial expenses such as "Car Purchase" and "Initial Repairs" are excluded from cash flow calculations.'}
                  </p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </CardTitle>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Select value={includeInitial ? "include" : "exclude"} onValueChange={(v) => setIncludeInitial(v === "include")}>
              <SelectTrigger className="w-auto min-w-[180px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="exclude">Exclude Initial Expenses</SelectItem>
                <SelectItem value="include">Include Initial Expenses</SelectItem>
              </SelectContent>
            </Select>
            <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
              <SelectTrigger className="w-auto min-w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="operations">Operations Only</SelectItem>
                <SelectItem value="all-vehicles">Vehicles Only</SelectItem>
                {vehicles?.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="month"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(value) => {
                  const abs = Math.abs(value);
                  if (abs >= 1000) return `${value < 0 ? "-" : ""}$${(abs / 1000).toFixed(1)}k`;
                  return `$${value}`;
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  color: "hsl(var(--popover-foreground))",
                }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  return (
                    <div style={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "var(--radius)",
                      color: "hsl(var(--popover-foreground))",
                      padding: "8px 12px",
                      fontSize: 13,
                    }}>
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                      <p>Earnings: {formatCurrency(data.earnings)}</p>
                      <p>Expenses: {formatCurrency(data.expenses)}</p>
                      <p style={{ fontWeight: 600, marginTop: 4 }}>
                        Cash Flow: {formatCurrency(data.cashFlow)}
                      </p>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
              <Bar dataKey="cashFlow">
                {chartData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.cashFlow >= 0 ? "hsl(var(--primary))" : "hsl(var(--destructive))"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data yet. Import trips and expenses to see cash flow.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
